import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, MapPin, Music2, X, Star, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { INSTRUMENTS, FEE_PREFS, EVENT_TYPES } from "../lib/instruments";
import { useAuth } from "../context/AuthContext";
import { localYmd } from "../lib/utils";
import { toast } from "sonner";

// Phase 1 beta: role determines which search tools you get, not tier.
// Musicians are locked to their own instrument (dep-finding).
// Bookers / conductors get cross-instrument search (ensemble management).
const CROSS_INSTRUMENT_ROLES = new Set(["booker", "conductor", "admin"]);

export default function SearchPage() {
  const { user } = useAuth();
  const canSearchAllInstruments = CROSS_INSTRUMENT_ROLES.has((user?.role || "").toLowerCase());
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(!canSearchAllInstruments);

  // Musicians are limited to their own instruments (primary + secondary).
  const allowedInstruments = useMemo(() => {
    if (canSearchAllInstruments) return null; // null = no restriction
    const primary = (profile?.instrument || "").trim();
    const secondary = (profile?.secondary_instruments || []).filter(Boolean);
    const list = [primary, ...secondary].filter(Boolean);
    // Dedupe (case-insensitive), preserving order.
    const seen = new Set();
    return list.filter((i) => {
      const k = i.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [canSearchAllInstruments, profile]);

  const [q, setQ] = useState({ instrument: "", date: "", slot: "any", location: "", fee_preference: "any", own_transport: false });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookFor, setBookFor] = useState(null);

  // Fetch profile only when we need to enforce instrument lock-in (i.e. musician role).
  useEffect(() => {
    if (canSearchAllInstruments) { setProfileLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/profile/me");
        if (!cancelled) setProfile(data || {});
      } catch {
        if (!cancelled) setProfile({});
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canSearchAllInstruments]);

  // Once we know the allowed instruments for a musician, default the dropdown to their primary.
  useEffect(() => {
    if (canSearchAllInstruments) return;
    if (!allowedInstruments || allowedInstruments.length === 0) return;
    setQ((prev) => prev.instrument ? prev : { ...prev, instrument: allowedInstruments[0] });
  }, [canSearchAllInstruments, allowedInstruments]);

  const noInstrumentOnProfile = !canSearchAllInstruments && !profileLoading && (allowedInstruments?.length ?? 0) === 0;

  const onSearch = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(q).forEach(([k, v]) => { if (v !== "" && v !== false && v !== "any") params.set(k, v); });
      const { data } = await api.get(`/search/musicians?${params.toString()}`);
      setResults(data || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.error === "musician_instrument_locked" || detail.error === "platinum_required")) {
        toast.error(detail.message || "Musicians can only search for deps on their own instrument.");
      } else {
        toast.error(formatApiErrorDetail(detail) || e.message);
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="search-page">
      <div>
        <div className="label-overline">Find players</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Who are you looking for?</h1>
        <p className="text-ink-600 mt-1 text-sm">Filter by instrument, date and travel. Players with no diary entry for that date appear as "Diary not marked" — their availability is unknown.</p>
      </div>

      {!canSearchAllInstruments && !profileLoading && (
        <div className="card-base p-4 sm:p-5 border-l-4 border-sage-400 bg-sage-50/60" data-testid="musician-scope-banner">
          <div className="flex items-start gap-3">
            <Lock size={18} className="text-sage-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-ink-900">Dep finder — your instrument only</div>
              <p className="text-sm text-ink-700 mt-1">
                {noInstrumentOnProfile ? (
                  <>Set your instrument on your <Link to="/profile" className="underline">profile</Link> to start finding deps.</>
                ) : (
                  <>You can search for cover on <strong>{allowedInstruments.join(" or ")}</strong>. Cross-instrument search is part of the booker / conductor toolkit.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={onSearch} className="card-base p-4 sm:p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label-overline block mb-2 flex items-center gap-1.5">
              Instrument
              {!canSearchAllInstruments && <Lock size={11} className="text-ink-500" data-testid="instrument-locked-icon" title="Dep finder — locked to your own instrument" />}
            </label>
            {canSearchAllInstruments ? (
              <select className="input-field" value={q.instrument} onChange={(e) => setQ({ ...q, instrument: e.target.value })} data-testid="search-instrument-select">
                <option value="">Any instrument</option>
                {INSTRUMENTS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            ) : (
              <select
                className="input-field disabled:opacity-100"
                value={q.instrument}
                onChange={(e) => setQ({ ...q, instrument: e.target.value })}
                disabled={noInstrumentOnProfile || (allowedInstruments?.length ?? 0) <= 1}
                data-testid="search-instrument-select"
              >
                {(allowedInstruments || []).length === 0 && <option value="">— set instrument on profile —</option>}
                {(allowedInstruments || []).map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            )}
          </div>
          <div>
            <label className="label-overline block mb-2">Location</label>
            <input className="input-field" placeholder="City, region" value={q.location} onChange={(e) => setQ({ ...q, location: e.target.value })} data-testid="search-location-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Date</label>
            <input type="date" className="input-field" value={q.date} onChange={(e) => setQ({ ...q, date: e.target.value })} data-testid="search-date-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Time</label>
            <select className="input-field" value={q.slot} onChange={(e) => setQ({ ...q, slot: e.target.value })} data-testid="search-slot-select">
              <option value="any">Any time</option>
              <option value="daytime">Daytime</option>
              <option value="evening">Evening</option>
            </select>
          </div>
          <div>
            <label className="label-overline block mb-2">Fee preference</label>
            <select className="input-field" value={q.fee_preference} onChange={(e) => setQ({ ...q, fee_preference: e.target.value })} data-testid="search-fee-select">
              {FEE_PREFS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 select-none cursor-pointer text-sm" data-testid="search-transport-label">
              <input type="checkbox" checked={q.own_transport} onChange={(e) => setQ({ ...q, own_transport: e.target.checked })} className="w-5 h-5 accent-sage-500" data-testid="search-transport-checkbox" />
              Has own transport
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="submit" className="btn-primary" disabled={busy || noInstrumentOnProfile} data-testid="search-submit-btn">
            <SearchIcon size={16} /> {busy ? "Searching…" : "Search"}
          </button>
        </div>
      </form>

      {results && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold">{results.length} musician{results.length === 1 ? "" : "s"}</h2>
          </div>
          {results.length === 0 ? (
            <div className="card-base p-6 text-ink-600" data-testid="search-empty">No musicians match those filters. Try widening the date, instrument, or travel radius.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {results.map((p) => (
                <article key={p.user_id} className="card-base p-4 flex items-start justify-between gap-3 hover:-translate-y-[1px] transition" data-testid={`search-result-${p.user_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-sage-100 text-sage-600 grid place-items-center font-heading font-semibold">
                      {p.name?.[0] || "?"}
                    </div>
                    <div>
                      <div className="font-heading font-semibold inline-flex items-center gap-1.5">
                        {p.name}
                        {p.founding_member && (
                          <Star size={14} className="fill-terracotta-500 text-terracotta-500" data-testid={`founding-badge-${p.user_id}`} title="AMDS Founding Member" />
                        )}
                      </div>
                      <div className="text-sm text-ink-600 inline-flex items-center gap-1.5">
                        <Music2 size={14} className="text-sage-500" /> {p.instrument || "—"}
                      </div>
                      {p.location && (
                        <div className="text-xs text-ink-500 inline-flex items-center gap-1.5 mt-0.5">
                          <MapPin size={12} /> {p.location} · {p.travel_radius_km}km
                        </div>
                      )}
                      {p.available_on && (
                        <span className="pill bg-sage-100 text-sage-700 mt-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-sage-500" />
                          Available · {p.available_on.slot.replace("_", " ")}
                        </span>
                      )}
                      {!p.available_on && p.availability_status === "unknown" && (
                        <span className="pill bg-ivory-100 text-ink-600 mt-2" title="They haven't marked their diary for this date">
                          <span className="w-1.5 h-1.5 rounded-full bg-ink-400" />
                          Diary not marked
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link to={`/musicians/${p.user_id}`} className="btn-secondary text-xs" data-testid={`view-profile-${p.user_id}`}>View</Link>
                    {user?.role !== "musician" && (
                      <button onClick={() => setBookFor(p)} className="btn-primary text-xs" data-testid={`request-${p.user_id}`}>Request</button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {bookFor && <BookingModal musician={bookFor} initialDate={q.date} onClose={() => setBookFor(null)} />}
    </div>
  );
}

export function BookingModal({ musician, initialDate, onClose }) {
  const today = new Date(); const isoToday = localYmd(today);
  const [form, setForm] = useState({
    musician_id: musician.user_id,
    event_type: "rehearsal",
    date: initialDate || isoToday,
    start_time: "19:30",
    end_time: "21:30",
    venue: "",
    ensemble_name: "",
    repertoire: "",
    fee: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const start_at = new Date(`${form.date}T${form.start_time}:00`).toISOString();
      const end_at = new Date(`${form.date}T${form.end_time}:00`).toISOString();
      const payload = { ...form, start_at, end_at };
      delete payload.start_time; delete payload.end_time;
      await api.post("/bookings", payload);
      toast.success("Request sent");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="booking-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl sm:rounded-3xl p-6 shadow-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-overline">New booking request</div>
            <h3 className="font-heading text-xl font-semibold">For {musician.name}</h3>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="booking-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="label-overline block mb-2">Type</label>
              <select className="input-field" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="booking-type-select">
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-2">Date</label>
              <input type="date" className="input-field" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="booking-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-overline block mb-2">Start</label>
                <input type="time" className="input-field" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required data-testid="booking-start-input" />
              </div>
              <div>
                <label className="label-overline block mb-2">End</label>
                <input type="time" className="input-field" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required data-testid="booking-end-input" />
              </div>
            </div>
          </div>
          <div>
            <label className="label-overline block mb-2">Ensemble</label>
            <input className="input-field" value={form.ensemble_name} onChange={(e) => setForm({ ...form, ensemble_name: e.target.value })} placeholder="St. Aldate's Sinfonia" data-testid="booking-ensemble-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Venue</label>
            <input className="input-field" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Holywell Music Room, Oxford" data-testid="booking-venue-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Repertoire (optional)</label>
            <input className="input-field" value={form.repertoire} onChange={(e) => setForm({ ...form, repertoire: e.target.value })} placeholder="Brahms 4, Sibelius VC" data-testid="booking-rep-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Fee / expenses (optional)</label>
            <input className="input-field" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} placeholder="£60 + travel" data-testid="booking-fee-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Notes</label>
            <textarea rows={3} className="input-field" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any extra context, dress code, etc." data-testid="booking-notes-input" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="booking-submit-btn">{busy ? "Sending…" : "Send request"}</button>
        </form>
      </div>
    </div>
  );
}
